import React, { useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import classnames from "classnames";
import { useTranslation } from "@helpers/translate";
import { formatOrderDate, getPositionPnlRoi } from "@helpers/formatOrders";
import InfoCircleIcon from "@assets/icons/InfoCircleIcon";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import { ReactComponent as ArrowFromSquare } from "@assets/images/icons/arrow-from-square.svg";
import { ReactComponent as ExclamationCircleIcon } from "@assets/images/icons/exclamation-circle.svg";
import glowImage from "@assets/images/futures-modals/close-trade-glow.jpg";
import { getPairMeta } from "../Quotes/pairMeta";
import { LeveragePicker } from "../Controls/MobileTradeForm/components/LeveragePicker";
import { formatUsd } from "../Controls/TradeForm/utils/format";
import { Share } from "../Orders/Share";
import {
  formatDollarPrice,
  formatSignedPnl,
  formatTpSlAmount,
  getTone,
  getTriggerPnl,
  stripQuoteAsset,
  toNumber,
} from "../Orders/positionFormat";
import { TRADE_MODAL_MOBILE_QUERY } from "./constants";
import { TradeModal } from "./TradeModal";
import { DetailRow } from "./components/DetailRow";
import { ModalCloseButton } from "./components/ModalCloseButton";
import { PercentSlider } from "./components/PercentSlider";
import { usePendingAction } from "./hooks/usePendingAction";
import "./PartialCloseModal.sass";

// As on Moon: steps of 10, 100% is a full close, 0% only shows the "too small" error
const PERCENT_STEPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const FULL_CLOSE_PERCENT = 100;
const DEFAULT_PERCENT = FULL_CLOSE_PERCENT;
// Drops the float noise before rounding down, the backend does the same
const PERCENT_AMOUNT_DECIMALS = 6;

const Row = (props) => <DetailRow block="partial-close" {...props} />;

/**
 * What the modal shows about the open trade.
 */
const getPartialCloseInfo = (order, markPrice) => {
  const symbol = order.symbol ?? order.pair ?? "";
  const baseAsset = stripQuoteAsset(String(symbol).toUpperCase());
  const { pnlText, pnlValue, isSellSide } = getPositionPnlRoi(order, markPrice);
  const entryPrice =
    toNumber(order.entry_price ?? order.fill_price ?? order.price) ?? 0;
  const quantity = Math.abs(toNumber(order.quantity ?? order.qty) ?? 0);
  const getTpSl = (triggerPrice) =>
    formatTpSlAmount(
      getTriggerPnl(triggerPrice, entryPrice, quantity, isSellSide),
    );

  return {
    baseAsset,
    pairMeta: getPairMeta(symbol || `${baseAsset}USDT`),
    pnlText,
    isSellSide,
    entryPrice,
    wager: toNumber(order.margin) ?? 0,
    livePnl: Number.isFinite(pnlValue) ? pnlValue : toNumber(order.pnl),
    takeProfit: getTpSl(order.take_profit_trigger_price),
    stopLoss: getTpSl(order.stop_loss_trigger_price),
  };
};

/**
 * "Close Trade": choose how much of an open trade to close at market price
 * (docs/trade-logic.md 2.1 Close Bet).
 *
 * @param {object} props
 * @param {object|null} props.order open trade, the modal is shown while it's set
 * @param {number} [props.markPrice]
 * @param {() => void} props.onClose
 * @param {(order: object) => Promise<void>} props.onCloseTrade full close (100%)
 * @param {(order: object, percent: number) => Promise<void>} props.onPartialClose
 */
export const PartialCloseModal = ({
  order,
  markPrice,
  onClose,
  onCloseTrade,
  onPartialClose,
}) => {
  const { __ } = useTranslation();
  const isMobile = useMediaQuery(TRADE_MODAL_MOBILE_QUERY);
  const [percent, setPercent] = useState(DEFAULT_PERCENT);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isClosing, handleSubmit] = usePendingAction(() =>
    percent === FULL_CLOSE_PERCENT
      ? onCloseTrade(order)
      : onPartialClose(order, percent),
  );
  const orderId = order?.id;

  useEffect(() => {
    setPercent(DEFAULT_PERCENT);
  }, [orderId]);

  if (!order) return null;

  const {
    baseAsset,
    pairMeta,
    pnlText,
    isSellSide,
    entryPrice,
    wager,
    livePnl,
    takeProfit,
    stopLoss,
  } = getPartialCloseInfo(order, markPrice);
  const DirectionIcon = isSellSide ? TradeArrowDownIcon : TradeArrowUpIcon;
  const pnlTone = getTone(livePnl);
  // docs/trade-logic.md 2.1: amount to close = floor(amount × pct, 2)
  const closeAmount =
    Math.floor(Number((wager * percent).toFixed(PERCENT_AMOUNT_DECIMALS))) / 100;
  const isFullClose = percent === FULL_CLOSE_PERCENT;
  const isTooSmall = closeAmount <= 0;

  return (
    <TradeModal
      isOpen
      onClose={onClose}
      className="partial-close"
      labelledBy="partial-close-title"
    >
      <span className="partial-close__glow" aria-hidden="true">
        <img src={glowImage} alt="" />
      </span>

      <header className="partial-close__header">
        <span className="partial-close__header-spacer" />
        <h3 id="partial-close-title" className="partial-close__title">
          {__("futures.partial_close_modal.title")}
        </h3>
        <ModalCloseButton className="partial-close__close" onClick={onClose} />
      </header>

      <div className="partial-close__coin">
        {pairMeta?.icon ? (
          <img
            src={pairMeta.icon}
            alt={baseAsset || "coin"}
            className="partial-close__coin-icon"
          />
        ) : (
          <span className="partial-close__coin-icon" />
        )}
        <span
          className={classnames("partial-close__coin-badge", {
            "partial-close__coin-badge--short": isSellSide,
          })}
        >
          <DirectionIcon size={18} color="#FFFFFF" />
        </span>
      </div>

      <p className="partial-close__label">
        {__("futures.partial_close_modal.select_amount")}
      </p>
      <p className="partial-close__amount">{formatUsd(closeAmount)}</p>

      {isTooSmall && (
        <p className="partial-close__error">
          <ExclamationCircleIcon className="partial-close__error-icon" />
          {__("futures.partial_close_modal.too_small")}
        </p>
      )}

      <p className="partial-close__hint">
        <InfoCircleIcon size={16} />
        {__("futures.partial_close_modal.hint")}
      </p>

      {isMobile ? (
        <div className="partial-close__picker">
          <LeveragePicker
            key={orderId}
            value={percent}
            onChange={setPercent}
            steps={PERCENT_STEPS}
            formatStep={(step) => `${step}%`}
          />
        </div>
      ) : (
        <PercentSlider
          value={percent}
          onChange={setPercent}
          label={__("futures.partial_close_modal.select_amount")}
          min={PERCENT_STEPS[0]}
          max={FULL_CLOSE_PERCENT}
          step={PERCENT_STEPS[1] - PERCENT_STEPS[0]}
        />
      )}

      <div className="partial-close__details">
        <Row label={__("futures.community.entry")}>
          <span className="partial-close__muted">{baseAsset}</span>
          {formatDollarPrice(entryPrice)}
        </Row>
        <Row label={__("futures.partial_close_modal.wager_leverage")}>
          {formatUsd(wager)}
          <span className="partial-close__tone--up">{order.leverage}x</span>
        </Row>
        <Row label={__("futures.open_profit_loss")}>
          <span
            className={classnames({
              [`partial-close__tone--${pnlTone}`]: pnlTone,
            })}
          >
            {formatSignedPnl(livePnl)}
          </span>
          <button
            type="button"
            className="partial-close__share"
            onClick={() => setIsShareOpen(true)}
            aria-label={__("futures.share")}
          >
            <ArrowFromSquare />
          </button>
        </Row>
        {/* The position's liquidation price, as in the trades table */}
        <Row label={__("futures.liquidation_price")}>
          {formatDollarPrice(order.liq_price)}
        </Row>
        <Row label={__("futures.tp_sl.column")}>
          {takeProfit || stopLoss ? (
            <>
              <span className="partial-close__tone--up">{takeProfit}</span>
              <span className="partial-close__divider" />
              <span className="partial-close__tone--down">{stopLoss}</span>
            </>
          ) : (
            "—"
          )}
        </Row>
        <Row label={__("futures.partial_close_modal.placed_on")}>
          {formatOrderDate(order.created_at) ?? "—"}
        </Row>
      </div>

      <div className="partial-close__actions">
        <p className="partial-close__warning">
          <ExclamationCircleIcon className="partial-close__warning-icon" />
          <span className="partial-close__warning-text">
            {__("futures.partial_close_modal.warning")}
          </span>
        </p>
        <button
          type="button"
          className={classnames("partial-close__submit", {
            "partial-close__submit--loading": isClosing,
          })}
          onClick={handleSubmit}
          disabled={isClosing || isTooSmall}
        >
          {isFullClose
            ? __("futures.partial_close_modal.close_all")
            : __("futures.partial_close")}
        </button>
      </div>

      <Share
        active={isShareOpen}
        setActive={setIsShareOpen}
        order={order}
        pnl={pnlText}
        markPrice={markPrice}
      />
    </TradeModal>
  );
};
